import React, { useMemo } from "react";
import { get, sortBy, isEmpty } from "lodash";
import DesktopWalletMask from "@tvg/atomic-ui/_static/Masks/desktopWallet";
import {
  getPawsContent,
  getInfoPanelModal,
  getPawsACHAllowAddToggle,
  getAccountsOnFileMethods
} from "@tvg/sh-lib-paws/redux/selectors";
import { Store } from "redux";
import { useDispatch, useSelector } from "react-redux";
import { closeInfoPanelModal } from "@tvg/sh-lib-paws/redux/slices/infoPanelSlice";
import { PaymentType as PaymentTypeEnum } from "@tvg/ts-types/Payment";
import tvgConf from "@tvg/conf";
import { getMazoomaToggle } from "@tvg/sh-lib-paws/redux/toggles";
import useIsBraintreeEnabled from "@tvg/sh-lib-paws/hooks/useIsBraintreeEnabled";
import parseCapiMessage from "@tvg/utils/capiUtils";
import type { UnaryFn, OnNewMethodArgs } from "../../types";
import {
  PaymentType,
  PaymentTypeInfo,
  PaymentTypeItem,
  PaymentTypeTag,
  PaymentTypeOption
} from "../../types";
import DesktopListItem from "../DesktopListItem";
import {
  PaymentTable,
  PaymentWrapper,
  PaymentTypeRow
} from "./styled-components";
import DesktopCardsOnFile from "../DesktopCardsOnFile";
import AlertMessagesModal from "../AlertMessagesModal";
import { canAddNewAccount, getTagsByPaymentType } from "../../utils";
import { mapAllPaymentOptions, buildOtherMethods } from "./utils";
import type { Props } from "./types";

interface RenderPaymentListParams {
  methods: PaymentTypeItem[];
  allMethods: PaymentTypeItem[];
  methodsInfos: { [key in PaymentType]: PaymentTypeInfo };
  displayAddMethod: boolean;
  activeOption: string | null;
  setActiveOption: UnaryFn<string | null, void>;
  onNewPaymentMethod: UnaryFn<OnNewMethodArgs, void>;
  hasTooltips?: boolean;
  showMZMPm?: boolean;
  allowCreateACH?: boolean;
  tags?: PaymentTypeTag[];
  isBraintreeCCEnabled?: boolean;
  braintreeCards?: PaymentTypeOption[];
}

const renderPaymentList = ({
  methods,
  allMethods,
  methodsInfos,
  displayAddMethod,
  activeOption,
  setActiveOption,
  onNewPaymentMethod,
  hasTooltips = false,
  showMZMPm = true,
  allowCreateACH = false,
  tags = [],
  isBraintreeCCEnabled = false,
  braintreeCards = []
}: RenderPaymentListParams) =>
  sortBy(methods, "priority")
    .filter(
      (item) =>
        !(
          (item.paymentType === PaymentTypeEnum.MAZOOMA && !showMZMPm) ||
          (item.paymentType === PaymentTypeEnum.ECHECK &&
            !allowCreateACH &&
            isEmpty(item.paymentTypeOptions))
        )
    )
    .map((baseMethod: PaymentTypeItem) => {
      // When braintree is enabled, replace CCP cards with braintree cards
      const method =
        isBraintreeCCEnabled &&
        baseMethod.paymentType === PaymentTypeEnum.CREDIT_DEBIT_CARD
          ? { ...baseMethod, paymentTypeOptions: braintreeCards }
          : baseMethod;
      const canDisplayAddMethod = displayAddMethod
        ? canAddNewAccount(
            method.paymentType,
            get(method, "paymentTypeOptions", []),
            { allowCreateACH }
          )
        : false;

      const isOPM = method.paymentType === PaymentTypeEnum.OTHER_PAYMENT;
      const isDepositsAvailable = !!(isOPM
        ? method.depositsEnabled
        : method.depositsAvailable);
      const isWithdrawalsAvailable = !!(isOPM
        ? method.withdrawalsEnabled
        : method.withdrawalsAvailable);
      const isUnderMaintenance = isOPM
        ? method.paymentTypeOptions.every((pm) => !pm.paymentTypeAvailable)
        : !method.paymentTypeAvailable;

      return (
        <PaymentTypeRow
          data-qa-label={
            isOPM
              ? "otherPaymentMethodsRow"
              : `paymentTypeRow-${method.paymentType}`
          }
          key={method.paymentType}
        >
          <DesktopListItem
            paymentType={method.paymentType}
            isUnderMaintenance={isUnderMaintenance}
            {...methodsInfos[method.paymentType]}
            isDisabled={!isDepositsAvailable}
            tags={getTagsByPaymentType(tags, method.paymentType)}
          />
          <DesktopCardsOnFile
            hasTooltips={hasTooltips}
            activeOption={activeOption}
            setActiveOption={setActiveOption}
            addMethodText={get(
              methodsInfos[method.paymentType],
              "addAccountText",
              ""
            )}
            method={method}
            onNewPaymentMethod={onNewPaymentMethod}
            allPaymentMethodOptions={mapAllPaymentOptions(allMethods)}
            displayAddMethod={
              !!(
                canDisplayAddMethod &&
                isDepositsAvailable &&
                method.paymentTypeAvailable
              )
            }
            disableAddPM={!isDepositsAvailable || !method.paymentTypeAvailable}
            disableChildrenMethods={
              (!isDepositsAvailable && !isWithdrawalsAvailable) ||
              (!isOPM && !method.paymentTypeAvailable)
            }
          />
        </PaymentTypeRow>
      );
    });

const DesktopWallet = ({
  activeOption,
  setActiveOption,
  isLoading,
  methods,
  methodsInfos,
  otherMethods,
  onNewPaymentMethod
}: Props) => {
  const dispatch = useDispatch();
  const otherPaymentMethods = buildOtherMethods(otherMethods, methodsInfos);

  const isInfoPanelModalOpen = useSelector(getInfoPanelModal);
  const pawsLabels = useSelector(getPawsContent);
  const isMZMToggleOn = useSelector(getMazoomaToggle);
  const allowCreateACH = useSelector<Store, boolean>(getPawsACHAllowAddToggle);
  const isBraintreeCCEnabled = useIsBraintreeEnabled();
  const accountsOnFile: PaymentTypeOption[] =
    useSelector(getAccountsOnFileMethods) || [];
  const paymentTypeTags: PaymentTypeTag[] = useSelector((store) =>
    parseCapiMessage(store, "capi.messages.pawsPaymentTypeTags", {})
  );

  // Get braintree cards (BTCC/BTDB) from the filtered accounts on file
  const braintreeCards = useMemo(
    () =>
      accountsOnFile.filter(
        (card: PaymentTypeOption) =>
          card.paymentType === PaymentTypeEnum.BRAINTREE_CREDIT_CARD ||
          card.paymentType === PaymentTypeEnum.BRAINTREE_DEBIT_CARD
      ),
    [accountsOnFile]
  );

  const cardLimitsLabels = get(pawsLabels, "paymentMessageModal.cardLimit", {});

  const allMethods = useMemo(
    () => [...methods, ...otherPaymentMethods],
    [methods, otherPaymentMethods]
  );
  return (
    <PaymentWrapper data-qa-label="walletWrapper">
      {isLoading ? (
        <DesktopWalletMask />
      ) : (
        <>
          <PaymentTable>
            {renderPaymentList({
              methods,
              allMethods,
              methodsInfos,
              displayAddMethod: true,
              activeOption,
              setActiveOption,
              onNewPaymentMethod,
              hasTooltips: true,
              showMZMPm: isMZMToggleOn,
              allowCreateACH,
              tags: paymentTypeTags,
              isBraintreeCCEnabled,
              braintreeCards
            })}
            {otherPaymentMethods[0] &&
              otherPaymentMethods[0].paymentTypeOptions.length > 0 &&
              renderPaymentList({
                methods: otherPaymentMethods,
                allMethods,
                methodsInfos,
                displayAddMethod: false,
                activeOption,
                setActiveOption,
                onNewPaymentMethod,
                hasTooltips: true,
                showMZMPm: isMZMToggleOn,
                allowCreateACH,
                tags: paymentTypeTags,
                isBraintreeCCEnabled,
                braintreeCards
              })}
          </PaymentTable>
          <AlertMessagesModal
            isMobile={tvgConf().device === "mobile"}
            isOpen={isInfoPanelModalOpen as boolean}
            qaLabel="alert-messages"
            title={cardLimitsLabels.title}
            description={cardLimitsLabels.description}
            type="warning"
            onCloseMethod={() => dispatch(closeInfoPanelModal())}
            actions={[
              {
                text: cardLimitsLabels.primaryButton,
                onClick: () => dispatch(closeInfoPanelModal()),
                isStretched: true,
                isUppercase: false,
                qaLabel: "close"
              }
            ]}
          />
        </>
      )}
    </PaymentWrapper>
  );
};

export default DesktopWallet;
